import { Link, useNavigate } from "react-router-dom"
import { useRef, useState } from "react";
import { handleError, useAuth } from "../../modules/authentication/AuthProvider";
import { Input, Button, Alert, Checkbox } from "react-daisyui";
import routes from "../../modules/routes/Routes";
import { AuthError } from "@supabase/supabase-js";
import AuthenticationScreen from "./AuthenticationScreen";
import { useTranslation } from 'react-i18next';

const Registration: React.FC = () => {
    const { signup } = useAuth();
    const { t } = useTranslation();

    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);
    const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
    const [acceptEmailUpdates, setAcceptEmailUpdates] = useState(false);

    const [emailError, setEmailError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
    const [privacyPolicyError, setPrivacyPolicyError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useNavigate();

    async function handleAuthError(error: AuthError) {
        const handledError = handleError(error);

        console.log({ error, authError: handledError })

        if (handledError) {
            handledError.field === 'password'
                ? setPasswordError(handledError.message)
                : setEmailError(handledError.message);
        }
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        setEmailError(null);
        setPasswordError(null);
        setConfirmPasswordError(null);
        setPrivacyPolicyError(null);

        if (emailRef.current && passwordRef.current && confirmPasswordRef.current) {
            if (passwordRef.current.value !== confirmPasswordRef.current.value) {
                return setConfirmPasswordError('Passwörter stimmen nicht überein.');
            }

            if (!acceptPrivacyPolicy) {
                return setPrivacyPolicyError('Bitte akzeptieren Sie die Datenschutzerklärung.');
            }

            setLoading(true);

            const credential = await signup(emailRef.current.value, passwordRef.current.value, acceptEmailUpdates);

            if (credential.error !== null)
                handleAuthError(credential.error)
            else
                navigate(routes.app.gettingStarted.build() + '#confirmEmail');
        }
        setLoading(false);
    }

    return (
        <AuthenticationScreen title={t('authentication.registration.title')}>
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <div className="flex flex-col gap-3">
                    <Input
                        ref={emailRef}
                        type="email"
                        placeholder={t('authentication.registration.emailPlaceholder')}
                        color={emailError !== null ? "error" : undefined}
                        data-testid="registration-email"
                        required
                    />
                    <Input
                        ref={passwordRef}
                        type="password"
                        placeholder={t('authentication.registration.passwordPlaceholder')}
                        color={passwordError !== null ? "error" : undefined}
                        data-testid="registration-password"
                        required
                    />
                    <Input
                        ref={confirmPasswordRef}
                        type="password"
                        placeholder={t('authentication.registration.confirmPasswordPlaceholder')}
                        color={confirmPasswordError !== null ? "error" : undefined}
                        data-testid="registration-confirm-password"
                        required
                    />
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="acceptPrivacyPolicy"
                            checked={acceptPrivacyPolicy}
                            onChange={(e) => setAcceptPrivacyPolicy(e.target.checked)}
                            data-testid="registration-privacy-policy"
                        />
                        <label htmlFor="acceptPrivacyPolicy" className="text-sm">
                            {t('authentication.registration.acceptPrivacyPolicy')} <a href="/privacypolicy" target="_blank" rel="noopener noreferrer" className="text-primary">{t('authentication.registration.privacyPolicyLink')}</a>
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="acceptEmailUpdates"
                            checked={acceptEmailUpdates}
                            onChange={(e) => setAcceptEmailUpdates(e.target.checked)}
                            data-testid="registration-email-updates"
                        />
                        <label htmlFor="acceptEmailUpdates" className="text-sm">
                            {t('authentication.registration.acceptEmailUpdates')}
                        </label>
                    </div>
                    
                    <Button type="submit" color="primary" disabled={loading} data-testid="registration-submit">
                        {loading ? t('authentication.registration.registering') : t('authentication.registration.registerButton')}
                    </Button>

                    {(emailError !== null || passwordError !== null || confirmPasswordError !== null || privacyPolicyError !== null) && (
                        <Alert color="error" data-testid="registration-error">
                            {emailError ?? passwordError ?? confirmPasswordError ?? privacyPolicyError}
                        </Alert>
                    )}

                    <p className="mt-3 text-sm">
                        {t('authentication.registration.alreadyRegistered')} <Link to={routes.authentication.login.build()} className="text-primary">{t('authentication.registration.loginNow')}</Link>
                    </p>
                </div>
            </form>
        </AuthenticationScreen>
    );
}

export default Registration;
